import React, { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";

import ReusableList from "../../../components/ReusableList";
import useProspects from "../../../hooks/useProspects";
import { OnboardingState } from "../../../types/onboarding";
import {
  PIPELINE_STATES,
  MORE_INFO_NEEDED,
  RESIGNED,
  stateLabel,
} from "../../../utils/onboardingStates";
import { ProspectType, SetupFeeStatus, StationReadiness } from "../../../types/onboarding";
import { PROSPECT_TYPE_OPTIONS } from "../../../utils/prospectType";
import { SETUP_FEE_STATUS_OPTIONS } from "../../../utils/setupFee";
import { prospectColumns, prospectSearchText } from "./prospectColumns";
import StageCounts from "./components/StageCounts";

// The readiness values an agent filters by. "Ready to publish" and "Waiting for
// support" are the queues waiting on an agent rather than on a customer
// (CWA-154, CWA-228).
const READINESS_OPTIONS = [
  { label: "All readiness", value: null },
  { label: "Ready to publish", value: "READY_TO_PUBLISH" },
  { label: "Waiting for support", value: "WAITING_FOR_SUPPORT" },
  { label: "Waiting for the customer", value: "AWAITING_CONFIGURATION" },
  { label: "Live", value: "LIVE" },
  { label: "No stations yet", value: "NO_STATIONS" },
];

// CWA-189: the reminders escalated and nobody has logged a call yet.
const FOLLOW_UP_OPTIONS = [
  { label: "All follow-ups", value: false },
  { label: "Needs follow-up", value: true },
];

const STATE_OPTIONS = [
  { label: "All statuses", value: null },
  ...[...PIPELINE_STATES, RESIGNED, MORE_INFO_NEEDED].map((s) => ({
    label: stateLabel(s),
    value: s,
  })),
];

/**
 * Prospects list (US1): all prospects with summary fields + multi-station /
 * more-info flags, filterable by onboarding state. No sensitive fields here.
 */
const ProspectsListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const {
    prospects,
    loading,
    error,
    stateFilter,
    setStateFilter,
    readinessFilter,
    setReadinessFilter,
    setupFeeFilter,
    setSetupFeeFilter,
    typeFilter,
    setTypeFilter,
    followUpFilter,
    setFollowUpFilter,
    counts,
  } = useProspects({ toastRef: toast });
  // The search box matches names, company and station IDs (CWA-208).
  const rows = useMemo(() => prospects.map((p) => ({ ...p, search_text: prospectSearchText(p) })), [prospects]);

  const filter = (
    <div className="flex gap-2 flex-wrap">
      <Dropdown
        value={stateFilter}
        options={STATE_OPTIONS}
        onChange={(e) => setStateFilter(e.value as OnboardingState | null)}
        placeholder="Filter by status"
        className="w-full sm:w-15rem"
      />
      <Dropdown
        value={readinessFilter}
        options={READINESS_OPTIONS}
        onChange={(e) =>
          setReadinessFilter(e.value as StationReadiness | null)
        }
        placeholder="Filter by readiness"
        className="w-full sm:w-15rem"
        aria-label="Filter by station readiness"
      />
      <Dropdown
        value={setupFeeFilter}
        options={[
          { label: "All setup fees", value: null },
          ...SETUP_FEE_STATUS_OPTIONS,
        ]}
        onChange={(e) => setSetupFeeFilter(e.value as SetupFeeStatus | null)}
        placeholder="Filter by setup fee"
        className="w-full sm:w-15rem"
        aria-label="Filter by setup fee"
      />
      <Dropdown
        value={typeFilter}
        options={PROSPECT_TYPE_OPTIONS}
        onChange={(e) => setTypeFilter(e.value as ProspectType | null)}
        placeholder="Filter by onboarding type"
        className="w-full sm:w-15rem"
        aria-label="Filter by onboarding type"
      />
      <Dropdown
        value={followUpFilter}
        options={FOLLOW_UP_OPTIONS}
        onChange={(e) => setFollowUpFilter(e.value === true)}
        placeholder="Filter by follow-up"
        className="w-full sm:w-15rem"
        aria-label="Filter by follow-up"
      />
    </div>
  );

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />
      <h2 className="mt-0">Prospects</h2>

      {error && (
        <Message
          severity="error"
          text="Prospects could not be loaded. Please try again."
          className="mb-3 w-full"
        />
      )}

      <StageCounts
        counts={counts}
        selected={stateFilter}
        onSelect={setStateFilter}
      />

      <ReusableList
        data={rows}
        loading={loading}
        searchable
        globalFilterFields={["search_text"]}
        actionButtons={filter}
        columns={prospectColumns}
        onRowClick={(e: any) =>
          navigate(`/onboarding/prospects/${e?.data?.id}`)
        }
        emptyMessage="No prospects yet."
      />
    </div>
  );
};

export default ProspectsListPage;
